import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Menu, Space, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faFileExport,
  faTrash,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateChannel, updateOpenModalDeleteChannel } from "../../../redux/channels/channelSlice";
// WIZARD NAVIGATION
import { WizardStep } from "../../setupWizard/utils/wizardInvalidation";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTH
import downloadEntityAsBinary from "../../../utils/exportEntity";
import { formatUuidT } from "../../../flatbuffers/display";

function ChannelsTableActions({ selectedItem }: any) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenModalDelete = () => {
    dispatch(updateChannel(selectedItem));
    dispatch(updateOpenModalDeleteChannel(true));
  };
  const handleOpenModalEdit = () => {
    const orgId = formatUuidT(selectedItem.organizationId) || "";
    if (orgId) {
      navigate(`/organization-management/setup-wizard?orgId=${orgId}&step=${WizardStep.Channels}`);
    }
  };

  const handleExportChannelBinary = async () => {
    if (!globalOrganizationId) {
      message.error("No organization selected.");
      return;
    }
    try {
      await downloadEntityAsBinary(globalOrganizationId, "channels", selectedItem.name);
    } catch {
      message.error("Failed to export channel.");
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const EDIT = hasPermission(Permission.ChannelsEdit, permissionSet ?? [])
      ? {
          label: (
            <div
              className="generalStyles__tableActionBox"
              role="button"
              tabIndex={0}
              onClick={() => {
                handleOpenModalEdit();
              }}
              onKeyDown={() => {
                handleOpenModalEdit();
              }}
            >
              <span>
                <FontAwesomeIcon
                  className="generalStyles__info generalStyles__mrFix"
                  icon={faPenToSquare}
                />
                {t("general.edit")}
              </span>
            </div>
          ),
          key: "1",
        }
      : null;

    const DELETE = hasPermission(Permission.ChannelsDelete, permissionSet ?? [])
      ? {
          label: (
            <div
              className="generalStyles__tableActionBox"
              role="button"
              tabIndex={0}
              onClick={() => {
                handleOpenModalDelete();
              }}
              onKeyDown={() => {
                handleOpenModalDelete();
              }}
            >
              <span>
                <FontAwesomeIcon
                  className="generalStyles__info generalStyles__mrFix"
                  icon={faTrash}
                />
                {t("general.delete")}
              </span>
            </div>
          ),
          key: "2",
        }
      : null;

    const EXPORT = hasPermission(Permission.ChannelsView, permissionSet ?? [])
      ? {
          label: (
            <span>
              <FontAwesomeIcon
                className="generalStyles__info generalStyles__mrFix"
                icon={faFileExport}
              />
              {t("organizationManagement.exportDescription")}
            </span>
          ),
          key: "3",
          onClick: handleExportChannelBinary,
        }
      : null;

    return [EDIT, EXPORT, DELETE];
  };

  const menu = <Menu items={manageActions()} />;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown dropdownRender={() => menu} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default ChannelsTableActions;
